import { useParams, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { projects } from "@/data/projectsData";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight } from "lucide-react";
import NotFound from "./NotFound";

const ProjectDetail = () => {
    const { id } = useParams();
    const projectIndex = projects.findIndex((p) => p.id === id);
    const project = projects[projectIndex];

    if (!project) {
        return <NotFound />;
    }

    const prevProject = projects[projectIndex - 1] || projects[projects.length - 1];
    const nextProject = projects[projectIndex + 1] || projects[0];

    const renderProjectVisuals = (visuals: any[], sectionTitle: string, isFullWidthLandscape = false) => {
        if (!visuals || visuals.length === 0) return null;

        const rows: any[][] = [];
        let currentRow: any[] = [];

        visuals.forEach((vis, idx) => {
            if (vis.aspect === "landscape") {
                if (currentRow.length > 0) {
                    rows.push(currentRow);
                    currentRow = [];
                }
                rows.push([vis]);
            } else {
                currentRow.push(vis);
                if (currentRow.length === 2) {
                    rows.push(currentRow);
                    currentRow = [];
                }
            }
        });

        if (currentRow.length > 0) {
            rows.push(currentRow);
        }

        return (
            <div className="flex flex-col gap-3 lg:gap-4">
                {rows.map((row, rIdx) => {
                    const isLandscapeRow = row.length === 1 && row[0].aspect === "landscape";

                    return (
                        <div
                            key={rIdx}
                            className="w-full overflow-hidden"
                        >
                            <div className={`grid gap-2 ${row.length === 2 ? "grid-cols-2" : "grid-cols-1"
                                }`}>
                                {row.map((vis, vIdx) => (
                                    <div
                                        key={vIdx}
                                        className="bg-transparent overflow-hidden rounded-2xl flex justify-center items-start"
                                    >
                                        {vis.type === "youtube" ? (
                                            <motion.div
                                                initial={{ y: 40, opacity: 0 }}
                                                whileInView={{ y: 0, opacity: 1 }}
                                                viewport={{ once: true, margin: "-100px" }}
                                                transition={{ duration: 1.2, delay: vIdx * 0.15, ease: [0.23, 1, 0.32, 1] }}
                                                className="relative w-full aspect-video"
                                            >
                                                <iframe
                                                    src={`https://www.youtube.com/embed/${vis.videoId}`}
                                                    title={`${sectionTitle} - Video ${vIdx}`}
                                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                    allowFullScreen
                                                    className="absolute inset-0 w-full h-full rounded-2xl"
                                                />
                                            </motion.div>
                                        ) : (
                                            <motion.img
                                                initial={{ y: 40, opacity: 0 }}
                                                whileInView={{ y: 0, opacity: 1 }}
                                                viewport={{ once: true, margin: "-100px" }}
                                                transition={{ duration: 1.2, delay: vIdx * 0.15, ease: [0.23, 1, 0.32, 1] }}
                                                src={vis.url}
                                                alt={`${sectionTitle} - ${vIdx}`}
                                                className="max-w-full h-auto block mx-auto"
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <Layout>
            {/* Hero Section */}
            <section className="pt-32 pb-12 lg:pt-48 lg:pb-20 bg-black">
                <div className="container-wide px-4 md:px-8">
                    <div className="grid lg:grid-cols-12 gap-12 items-end">
                        <div className="lg:col-span-8">
                            <div className="overflow-hidden mb-8">
                                <motion.h1
                                    initial={{ y: "100%" }}
                                    animate={{ y: 0 }}
                                    transition={{ duration: 1.5, ease: [0.23, 1, 0.32, 1] }}
                                    className="display-md text-white"
                                >
                                    {project.title}
                                </motion.h1>
                            </div>
                            <div className="overflow-hidden">
                                <motion.p
                                    initial={{ y: "100%" }}
                                    animate={{ y: 0 }}
                                    transition={{ duration: 1.5, delay: 0.1, ease: [0.23, 1, 0.32, 1] }}
                                    className="text-2xl lg:text-4xl text-white/60 font-light max-w-2xl leading-tight"
                                >
                                    {project.tagline}
                                </motion.p>
                            </div>
                        </div>

                        <div className="lg:col-span-4 border-l border-white/10 pl-8 pb-4">
                            <div className="space-y-8">
                                <div>
                                    <div className="overflow-hidden mb-4">
                                        <motion.h4
                                            initial={{ y: "100%" }}
                                            animate={{ y: 0 }}
                                            transition={{ duration: 1.2, delay: 0.3, ease: [0.23, 1, 0.32, 1] }}
                                            className="text-purple text-xs font-bold tracking-[0.2em]"
                                        >
                                            Services
                                        </motion.h4>
                                    </div>
                                    <ul className="space-y-1">
                                        {project.services.map((s, idx) => (
                                            <div key={s} className="overflow-hidden">
                                                <motion.li
                                                    initial={{ y: "100%" }}
                                                    animate={{ y: 0 }}
                                                    transition={{ duration: 1.2, delay: 0.4 + (idx * 0.05), ease: [0.23, 1, 0.32, 1] }}
                                                    className="text-white font-light"
                                                >
                                                    {s}
                                                </motion.li>
                                            </div>
                                        ))}
                                    </ul>
                                </div>
                                <div>
                                    <div className="overflow-hidden mb-4">
                                        <motion.h4
                                            initial={{ y: "100%" }}
                                            animate={{ y: 0 }}
                                            transition={{ duration: 1.2, delay: 0.5, ease: [0.23, 1, 0.32, 1] }}
                                            className="text-purple text-xs font-bold tracking-[0.2em]"
                                        >
                                            Industry
                                        </motion.h4>
                                    </div>
                                    <div className="overflow-hidden">
                                        <motion.p
                                            initial={{ y: "100%" }}
                                            animate={{ y: 0 }}
                                            transition={{ duration: 1.2, delay: 0.6, ease: [0.23, 1, 0.32, 1] }}
                                            className="text-white font-light"
                                        >
                                            {project.industry}
                                        </motion.p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Featured Hero Image */}
            <section className="pb-8 lg:pb-16 bg-black">
                <div className="container-wide px-4 md:px-8">
                    <div className="aspect-[21/9] bg-white/5 rounded-2xl overflow-hidden relative">
                        <motion.img
                            initial={{ y: "100%" }}
                            animate={{ y: 0 }}
                            transition={{ duration: 1.8, ease: [0.23, 1, 0.32, 1] }}
                            src={project.heroImage}
                            alt={project.title}
                            className="w-full h-full object-cover"
                        />
                    </div>
                </div>
            </section>

            {/* Narrative Sections */}
            <div className="bg-black text-white overflow-x-hidden">
                {/* Overview */}
                <section className="pt-4 pb-8 lg:pt-6 lg:pb-12">
                    <div className="container-wide px-4 md:px-8">
                        <div className="lg:max-w-xl lg:pl-24">
                            {!project.hideSectionHeaders && (
                                <div className="overflow-hidden mb-8">
                                    <motion.span
                                        initial={{ y: "100%" }}
                                        whileInView={{ y: 0 }}
                                        viewport={{ once: true, margin: "-50px" }}
                                        transition={{ duration: 1.2, ease: [0.23, 1, 0.32, 1] }}
                                        className="label bg-purple text-white px-3 py-1 inline-block rounded-sm text-xs font-bold tracking-[0.1em]"
                                    >
                                        01. Overview
                                    </motion.span>
                                </div>
                            )}
                            <div className="overflow-hidden">
                                <motion.div
                                    initial={{ y: "100%" }}
                                    whileInView={{ y: 0 }}
                                    viewport={{ once: true, margin: "-50px" }}
                                    transition={{ duration: 1.5, ease: [0.23, 1, 0.32, 1] }}
                                    className="prose prose-xl prose-invert max-w-none"
                                >
                                    <p className="text-lg md:text-xl text-white/60 leading-relaxed font-light">
                                        {project.overview}
                                    </p>
                                </motion.div>
                            </div>
                        </div>
                    </div>

                    {/* Overview Visuals */}
                    {project.overviewVisuals && project.overviewVisuals.length > 0 && (
                        <div className="container-wide px-4 md:px-8 mt-8 lg:mt-10">
                            {renderProjectVisuals(project.overviewVisuals, "Overview", false)}
                        </div>
                    )}
                </section>

                {/* The Challenge */}
                <section className="pt-4 pb-8 lg:pt-6 lg:pb-12 bg-white/5">
                    <div className="container-wide px-4 md:px-8">
                        <div className="lg:max-w-xl lg:pl-24">
                            {!project.hideSectionHeaders && (
                                <div className="overflow-hidden mb-8">
                                    <motion.span
                                        initial={{ y: "100%" }}
                                        whileInView={{ y: 0 }}
                                        viewport={{ once: true, margin: "-50px" }}
                                        transition={{ duration: 1.2, ease: [0.23, 1, 0.32, 1] }}
                                        className="label bg-purple text-white px-3 py-1 inline-block rounded-sm text-xs font-bold tracking-[0.1em]"
                                    >
                                        02. The Challenge
                                    </motion.span>
                                </div>
                            )}
                            <div className="overflow-hidden">
                                <motion.div
                                    initial={{ y: "100%" }}
                                    whileInView={{ y: 0 }}
                                    viewport={{ once: true, margin: "-50px" }}
                                    transition={{ duration: 1.5, ease: [0.23, 1, 0.32, 1] }}
                                    className="prose prose-lg prose-invert max-w-none"
                                >
                                    <p className="text-lg md:text-xl text-white/60 leading-relaxed font-light">
                                        {project.challenge}
                                    </p>
                                </motion.div>
                            </div>
                        </div>
                    </div>

                    {/* Challenge Visuals */}
                    {project.challengeVisuals && project.challengeVisuals.length > 0 && (
                        <div className="container-wide px-4 md:px-8 mt-8 lg:mt-10">
                            {renderProjectVisuals(project.challengeVisuals, "Challenge", false)}
                        </div>
                    )}
                </section>

                {/* Combined Approach & Execution Rendering */}
                {[...project.approach, ...project.execution].map((section, idx) => (
                    <div key={section.id}>
                        <section className="pt-4 pb-8 lg:pt-6 lg:pb-12">
                            <div className="container-wide px-4 md:px-8 mb-12">
                                <div className="lg:max-w-xl lg:pl-24">
                                    {!project.hideSectionHeaders && section.title && (
                                        <div className="overflow-hidden mb-8">
                                            <motion.span
                                                initial={{ y: "100%" }}
                                                whileInView={{ y: 0 }}
                                                viewport={{ once: true, margin: "-50px" }}
                                                transition={{ duration: 1.2, ease: [0.23, 1, 0.32, 1] }}
                                                className="label bg-purple text-white px-3 py-1 inline-block rounded-sm text-xs font-bold tracking-[0.1em]"
                                            >
                                                {section.number || `0${idx + 3}`}. {section.title}
                                            </motion.span>
                                        </div>
                                    )}
                                    <div className="overflow-hidden">
                                        <motion.div
                                            initial={{ y: "100%" }}
                                            whileInView={{ y: 0 }}
                                            viewport={{ once: true, margin: "-50px" }}
                                            transition={{ duration: 1.5, ease: [0.23, 1, 0.32, 1] }}
                                            className="prose prose-lg prose-invert max-w-none"
                                        >
                                            <p className="text-lg md:text-xl leading-relaxed text-white/60 font-light">
                                                {section.content}
                                            </p>
                                        </motion.div>
                                    </div>
                                </div>
                            </div>

                            {/* Visuals Rendering Logic */}
                            {section.visuals && section.visuals.length > 0 && (
                                <div className="container-wide px-4 md:px-8 mt-8 lg:mt-10">
                                    {renderProjectVisuals(section.visuals, section.title, false)}
                                </div>
                            )}
                        </section>
                    </div>
                ))}

                {/* Outcomes */}
                <section className="pt-4 pb-8 lg:pt-6 lg:pb-12 bg-purple/10">
                    <div className="container-wide px-4 md:px-8">
                        {project.outcomes && (
                            <div className="lg:max-w-xl lg:pl-24">
                                {!project.hideSectionHeaders && (
                                    <div className="overflow-hidden mb-8">
                                        <motion.span
                                            initial={{ y: "100%" }}
                                            whileInView={{ y: 0 }}
                                            viewport={{ once: true, margin: "-50px" }}
                                            transition={{ duration: 1.2, ease: [0.23, 1, 0.32, 1] }}
                                            className="label bg-purple text-white px-3 py-1 inline-block rounded-sm text-xs font-bold tracking-[0.1em]"
                                        >
                                            Conclusion
                                        </motion.span>
                                    </div>
                                )}
                                {!project.hideSectionHeaders && (
                                    <div className="overflow-hidden mb-8">
                                        <motion.h3
                                            initial={{ y: "100%" }}
                                            whileInView={{ y: 0 }}
                                            viewport={{ once: true, margin: "-50px" }}
                                            transition={{ duration: 1.5, ease: [0.23, 1, 0.32, 1] }}
                                            className="text-2xl md:text-3xl lg:text-4xl font-bold leading-tight text-white"
                                        >
                                            Outcomes
                                        </motion.h3>
                                    </div>
                                )}
                                <div className="overflow-hidden mb-12">
                                    <motion.p
                                        initial={{ y: "100%" }}
                                        whileInView={{ y: 0 }}
                                        viewport={{ once: true, margin: "-50px" }}
                                        transition={{ duration: 1.5, delay: 0.1, ease: [0.23, 1, 0.32, 1] }}
                                        className="text-lg md:text-xl leading-relaxed text-white/60 font-light"
                                    >
                                        {project.outcomes}
                                    </motion.p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Outcomes Visuals */}
                    {project.outcomesVisuals && project.outcomesVisuals.length > 0 && (
                        <div className="container-wide px-4 md:px-8 mt-8 lg:mt-10">
                            {renderProjectVisuals(project.outcomesVisuals, "Outcomes", false)}
                        </div>
                    )}

                    {!project.hideSectionHeaders && project.takeaways && (
                        <div className="container-wide px-4 md:px-8">
                            <div className="lg:max-w-xl lg:pl-24">
                                <div className="pt-12 border-t border-white/10">
                                    <div className="overflow-hidden mb-8">
                                        <motion.span
                                            initial={{ y: "100%" }}
                                            whileInView={{ y: 0 }}
                                            viewport={{ once: true, margin: "-50px" }}
                                            transition={{ duration: 1.2, ease: [0.23, 1, 0.32, 1] }}
                                            className="label bg-purple text-white px-3 py-1 inline-block rounded-sm text-xs font-bold tracking-[0.1em]"
                                        >
                                            Key Takeaway
                                        </motion.span>
                                    </div>
                                    <div className="overflow-hidden">
                                        <motion.p
                                            initial={{ y: "100%" }}
                                            whileInView={{ y: 0 }}
                                            viewport={{ once: true, margin: "-50px" }}
                                            transition={{ duration: 1.5, ease: [0.23, 1, 0.32, 1] }}
                                            className="text-xl lg:text-2xl font-light italic text-white leading-relaxed"
                                        >
                                            "{project.takeaways}"
                                        </motion.p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </section>

                {/* Project Navigation */}
                <section className="py-20">
                    <div className="container-wide px-4 md:px-8">
                        <div className="flex flex-col md:flex-row justify-between items-center gap-12 pt-20 border-t border-white/5">
                            <Link
                                to={`/projects/${prevProject.id}`}
                                className="group flex flex-col items-start gap-4"
                            >
                                <span className="text-xs font-bold tracking-[0.2em] text-white/40 group-hover:text-[#c0ff09] transition-colors flex items-center gap-2">
                                    <ArrowLeft size={16} /> PREVIOUS PROJECT
                                </span>
                                <span className="text-3xl lg:text-5xl font-bold text-white/80 group-hover:text-black transition-colors">
                                    {prevProject.title}
                                </span>
                            </Link>

                            <Link
                                to={`/projects/${nextProject.id}`}
                                className="group flex flex-col items-end gap-4 text-right"
                            >
                                <span className="text-xs font-bold tracking-[0.2em] text-white/40 group-hover:text-[#c0ff09] transition-colors flex items-center gap-2">
                                    NEXT PROJECT <ArrowRight size={16} />
                                </span>
                                <span className="text-3xl lg:text-5xl font-bold text-white/80 group-hover:text-black transition-colors">
                                    {nextProject.title}
                                </span>
                            </Link>
                        </div>
                    </div>
                </section>

                <section className="py-32 bg-black border-t border-white/10 text-center">
                    <div className="container-narrow px-4 md:px-8">
                        <h2 className="text-3xl md:text-5xl font-bold text-white mb-8">
                            Ready to establish your signal?
                        </h2>
                        <Button
                            size="lg"
                            className="bg-white text-black hover:bg-[#c0ff09] hover:text-black rounded-full px-10 py-8 text-lg font-bold transition-all duration-300"
                            asChild
                        >
                            <Link to="/contact">Start a Project with Us</Link>
                        </Button>
                    </div>
                </section>
            </div>
        </Layout>
    );
};

export default ProjectDetail;
